// The menu's entries, and on a phone which of them sit on the bottom tab bar and which in its "Menü" sheet.
import type { Me, FloorSummary, SellerSummary } from './types';
import type { IconName } from './ui/icons';

export interface NavItem {
  readonly path: string;
  readonly label: string;
  /** under the icon when the menu narrows to an icon rail (handhelds) or a phone's tab bar */
  readonly short?: string;
  readonly icon: IconName;
  readonly count?: (s: FloorSummary & SellerSummary) => number;
  /** work waiting there: a quiet count on a phone's tab (`count`, in red, is for problems) */
  readonly waiting?: (s: FloorSummary & SellerSummary) => number;
  /** only these roles see the item */
  readonly roles?: readonly Me['role'][];
}

/** A block of the menu: the rail draws a line between them, the phone's sheet a heading. */
export interface NavGroup<T extends NavItem = NavItem> {
  readonly title: string;
  readonly items: readonly T[];
}

/**
 * The floor screens a warehouse worker opens most, most used first: the first four that the role
 * may see become the phone's tabs, the next ones stand in for any it may not.
 */
export const FLOOR_TABS: readonly string[] = ['/toplama', '/paketleme', '/kabul', '/sevkiyat', '/yerlestirme', '/iade', '/sayim', '/sorunlu', '/'];
/** A seller's most opened pages, the same way. */
export const SELLER_TABS: readonly string[] = ['/', '/siparisler', '/iadeler', '/urunler', '/asn'];
export const TAB_COUNT = 4;

/** The entries this role sees: the rail's rule, and so the tab bar's and the sheet's. */
export function visibleTo<T extends NavItem>(items: readonly T[], role: Me['role']): T[] {
  return items.filter((item) => !item.roles || item.roles.includes(role));
}

/** Whether an entry is the open screen: "/" only on the summary, the others with their sub-pages. */
export function isActive(itemPath: string, routePath: string): boolean {
  if (itemPath === '/') return routePath === '/';
  return routePath === itemPath || routePath.startsWith(`${itemPath}/`);
}

/**
 * The phone's tabs (the preferred entries the role sees, filled up with the menu's next ones,
 * shown in the menu's own order) and the groups left for the "Menü" sheet.
 */
export function splitTabs<T extends NavItem>(
  groups: readonly NavGroup<T>[],
  preferred: readonly string[],
  count = TAB_COUNT,
): { tabs: T[]; rest: NavGroup<T>[] } {
  const all = groups.flatMap((g) => g.items);
  const chosen = new Set<string>();
  for (const path of [...preferred, ...all.map((item) => item.path)]) {
    if (chosen.size >= count) break;
    if (all.some((item) => item.path === path)) chosen.add(path);
  }
  return {
    tabs: all.filter((item) => chosen.has(item.path)),
    rest: groups.map((g) => ({ title: g.title, items: g.items.filter((item) => !chosen.has(item.path)) })).filter((g) => g.items.length > 0),
  };
}

/** What has the cursor, as far as an on-screen keyboard goes. */
export interface Focused {
  readonly tagName: string;
  readonly type?: string;
  readonly readOnly?: boolean;
  readonly isContentEditable?: boolean;
}

/** Input types a phone opens a picker for, or none at all, instead of its keyboard. */
const NO_KEYBOARD = new Set(['button', 'checkbox', 'color', 'date', 'datetime-local', 'file', 'hidden', 'image', 'month', 'radio', 'range', 'reset', 'submit', 'time', 'week']);

/** Whether the cursor is somewhere a phone types into (its keyboard comes up there). */
export function typesText(el: Focused | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA') return !el.readOnly;
  if (tag !== 'INPUT') return false;
  return !el.readOnly && !NO_KEYBOARD.has((el.type ?? 'text').toLowerCase());
}

/**
 * Whether the on-screen keyboard is up: something to type in has the cursor and the visible page
 * is a quarter or more shorter than it is at its tallest (the address bar sliding away is less).
 */
export function keyboardUp(typing: boolean, height: number, tallest: number): boolean {
  return typing && tallest > 0 && height < tallest * 0.75;
}
